const RightTires = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/tires-collections.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            How to Choose the Right Tires for Your Vehicle
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Your tires are the only contact point between your car and the road — make sure they’re the right ones for your safety, comfort, and performance.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            Selecting the right tires for your vehicle isn’t just about looks — it’s about ensuring optimal performance, safety, and longevity. With so many brands, tread styles, and ratings available, making the right choice can feel overwhelming. 
            At <strong>Express Tire Works</strong>, we’ve simplified the process to help you choose tires that match your driving needs, budget, and local road conditions.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. Understand Your Driving Needs
            </h2>
            <p>
              Every driver’s habits are different. Before buying new tires, think about how and where you drive:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>City driving:</strong> Focus on comfort and low road noise.</li>
              <li><strong>Highway commuting:</strong> Look for fuel-efficient, long-lasting tires.</li>
              <li><strong>Off-road or rural areas:</strong> Choose all-terrain or mud-terrain tires.</li>
              <li><strong>Performance enthusiasts:</strong> Go for high-performance or sport tires for improved handling.</li>
            </ul>
            <p>
              Matching your tires to your lifestyle ensures better traction, durability, and satisfaction.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Know Your Tire Size and Specs
            </h2>
            <p>
              Your vehicle’s ideal tire size can be found on the sidewall of your current tires or in your owner’s manual. A typical tire size looks like this: <strong>205/55R16 91V</strong>.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>205:</strong> Tire width in millimeters</li>
              <li><strong>55:</strong> Aspect ratio (height-to-width ratio)</li>
              <li><strong>R16:</strong> Wheel diameter in inches</li>
              <li><strong>91V:</strong> Load index and speed rating</li>
            </ul>
            <p>
              Always stick to the manufacturer’s recommended size for proper handling, braking, and ride comfort.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. Choose the Right Tire Type
            </h2>
            <p>There are several main categories of tires to consider:</p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>All-Season Tires:</strong> Versatile for year-round performance in mild climates.</li>
              <li><strong>Summer Tires:</strong> Ideal for hot, dry, and wet conditions — not for snow or ice.</li>
              <li><strong>Winter Tires:</strong> Designed for maximum grip in cold temperatures and snow.</li>
              <li><strong>Performance Tires:</strong> Built for agility, precision, and sporty driving.</li>
              <li><strong>All-Terrain Tires:</strong> Perfect for SUVs and trucks tackling mixed on/off-road use.</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. Compare Brands and Quality
            </h2>
            <p>
              Not all tires are created equal. Premium brands like Michelin, Bridgestone, Goodyear, and Continental offer advanced technology and longevity, while budget brands can still provide solid everyday performance.
            </p>
            <p className="mt-3">
              At Express Tire Works, we stock a wide range of trusted tire brands and models — so you always get the right balance between quality and price.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. Check the Tread Pattern
            </h2>
            <p>
              Tread design affects traction, noise, and handling. For example:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Symmetrical treads:</strong> Quiet, long-lasting, and good for general driving.</li>
              <li><strong>Asymmetrical treads:</strong> Great for wet and dry performance with stable cornering.</li>
              <li><strong>Directional treads:</strong> Best for wet traction and resistance to hydroplaning.</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              6. Consider Your Climate
            </h2>
            <p>
              Climate plays a major role in tire selection. Hot climates benefit from durable rubber compounds that resist heat, while snowy or icy regions demand winter tires with deeper grooves and softer rubber for grip.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              7. Budget Wisely
            </h2>
            <p>
              While it’s tempting to choose the cheapest option, consider the long-term value of quality tires — better traction, fuel efficiency, and longevity often make them more cost-effective in the long run.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              8. Get Professional Advice and Installation
            </h2>
            <p>
              Still unsure? Visit <strong>Express Tire Works</strong> — our experts can help you select the perfect tires for your vehicle, driving style, and budget. Once you order your tires online, you can schedule an in-house fitting at your convenience for a smooth, hassle-free installation.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              Choosing the right tires ensures better performance, improved safety, and a smoother driving experience. By understanding your needs and consulting with professionals, you’ll enjoy the road ahead — with confidence and peace of mind.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Selection</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Vehicle Safety</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Maintenance</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">All-Season Tires</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Performance Tires</p>
        </div>
      </section>
    </div>
  );
};

export default RightTires;